import React from 'react';
import { DashboardLayout, AuthLayout } from '@/components';
import { useSelector } from 'react-redux';
import { authStatus } from '@/services/redux/selectors';
import { Logo } from '@/components/common/logo';
import { Text } from '@/ui-kits';

export const PrivacyScreen = () => {
  const isAuthenticated = useSelector(authStatus);
  if (isAuthenticated) {
    return (
      <DashboardLayout>
        <PrivacyContent />
      </DashboardLayout>
    );
  }
  return (
    <AuthLayout isLayout={false}>
      <PrivacyContent />
    </AuthLayout>
  );
};

const PrivacyContent = () => {
  return (
    <div className="flex flex-col items-center w-full max-w-7xl mx-auto px-3 sm:px-4 -mt-10 md:-mt-12 pb-6 md:pb-8 lg:pb-16">
      {/* Security Policy Card */}
      <div className="w-full bg-white rounded-2xl md:rounded-3xl shadow-2xl p-4 sm:p-6 md:p-10 lg:p-12 mb-8 md:mb-12" style={{ boxShadow: '0px 10px 40px 0px rgba(0, 0, 0, 0.15)' }}>
        <Text variant="heading" className="font-FigtreeBold text-deep_blue mb-2 !text-2xl sm:!text-3xl md:!text-4xl">
          Security Policy
        </Text>
        <Text variant="free" className="text-deep_blue !font-FigtreeRegular mb-4 sm:mb-6 !text-sm sm:!text-base md:!text-md">
          Last Updated: 04 Feburary 2026
        </Text>
        <hr className="w-full border-t-2 border-ui_input_border_dark/40 mb-6 sm:mb-8" />

        {/* Information Security Policy - single column on mobile (1,2,3...), two columns on md+ */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 lg:gap-x-10 gap-y-6 md:gap-y-8">
          {/* 1. Purpose */}
          <div className="flex items-start gap-3 md:col-start-1">
            <Text variant="headingSemiBold" className="!text-lg sm:!text-xl md:!text-2xl text-deep_blue shrink-0">1.</Text>
            <div className="min-w-0 flex-1">
              <Text variant="headingSemiBold" className="!text-lg sm:!text-xl md:!text-2xl text-deep_blue mb-1 sm:mb-2">Purpose</Text>
              <Text variant="body" className="text-deep_blue !font-FigtreeRegular !text-xs sm:!text-sm leading-relaxed">
                This Information Security Policy defines the principles, controls, and responsibilities for protecting information
                assets of the Second Opinion platform. The objective is to ensure confidentiality, integrity, availability, and privacy
                of data, especially sensitive health information.
              </Text>
            </div>
          </div>
          {/* 2. Scope */}
          <div className="flex items-start gap-3 md:col-start-2">
            <Text variant="headingSemiBold" className="!text-lg sm:!text-xl md:!text-2xl text-deep_blue shrink-0">2.</Text>
            <div className="min-w-0 flex-1">
              <Text variant="headingSemiBold" className="!text-lg sm:!text-xl md:!text-2xl text-deep_blue mb-1 sm:mb-2">Scope</Text>
              <Text variant="body" className="text-deep_blue !font-FigtreeRegular !text-xs sm:!text-sm leading-relaxed">
                This policy applies to all information assets, users, systems, devices, applications, and cloud environments used by or
                on behalf of Second Opinion.
              </Text>
            </div>
          </div>
          {/* 3. Security Principles */}
          <div className="flex items-start gap-3 md:col-start-1">
            <Text variant="headingSemiBold" className="!text-lg sm:!text-xl md:!text-2xl text-deep_blue shrink-0">3.</Text>
            <div className="min-w-0 flex-1">
              <Text variant="headingSemiBold" className="!text-lg sm:!text-xl md:!text-2xl text-deep_blue mb-1 sm:mb-2">Security Principles</Text>
              <Text variant="body" className="text-deep_blue !font-FigtreeRegular !text-xs sm:!text-sm leading-relaxed">
                Confidentiality, Integrity, Availability, and Privacy by Design are core principles of this policy.
              </Text>
            </div>
          </div>
          {/* 4. Data Classification */}
          <div className="flex items-start gap-3 md:col-start-2">
            <Text variant="headingSemiBold" className="!text-lg sm:!text-xl md:!text-2xl text-deep_blue shrink-0">4.</Text>
            <div className="min-w-0 flex-1">
              <Text variant="headingSemiBold" className="!text-lg sm:!text-xl md:!text-2xl text-deep_blue mb-1 sm:mb-2">Data Classification</Text>
              <Text variant="body" className="text-deep_blue !font-FigtreeRegular !text-xs sm:!text-sm leading-relaxed">
              Public, Internal, Confidential, and Sensitive Personal / Health Data.
              </Text>
            </div>
          </div>
          {/* 5. Access Control */}
          <div className="flex items-start gap-3 md:col-start-1">
            <Text variant="headingSemiBold" className="!text-lg sm:!text-xl md:!text-2xl text-deep_blue shrink-0">5.</Text>
            <div className="min-w-0 flex-1">
              <Text variant="headingSemiBold" className="!text-lg sm:!text-xl md:!text-2xl text-deep_blue mb-1 sm:mb-2">Access Control</Text>
              <Text variant="body" className="text-deep_blue !font-FigtreeRegular !text-xs sm:!text-sm leading-relaxed">
                Role-based access, least privilege, MFA for privileged users, periodic access reviews.
              </Text>
            </div>
          </div>
          {/* 6. Authentication */}
          <div className="flex items-start gap-3 md:col-start-2">
            <Text variant="headingSemiBold" className="!text-lg sm:!text-xl md:!text-2xl text-deep_blue shrink-0">6.</Text>
            <div className="min-w-0 flex-1">
              <Text variant="headingSemiBold" className="!text-lg sm:!text-xl md:!text-2xl text-deep_blue mb-1 sm:mb-2">Authentication</Text>
              <Text variant="body" className="text-deep_blue !font-FigtreeRegular !text-xs sm:!text-sm leading-relaxed">
              Strong passwords, secure hashing, session controls. 
              </Text>
            </div>
          </div>
          {/* 7. Data Protection */}
          <div className="flex items-start gap-3 md:col-start-1">
            <Text variant="headingSemiBold" className="!text-lg sm:!text-xl md:!text-2xl text-deep_blue shrink-0">7.</Text>
            <div className="min-w-0 flex-1">
              <Text variant="headingSemiBold" className="!text-lg sm:!text-xl md:!text-2xl text-deep_blue mb-1 sm:mb-2">Data Protection</Text>
              <Text variant="body" className="text-deep_blue !font-FigtreeRegular !text-xs sm:!text-sm leading-relaxed">
                TLS encryption in transit, AES-256 at rest, secure key management.
              </Text>
            </div>
          </div>
          {/* 8. Application & Infrastructure Security */}
          <div className="flex items-start gap-3 md:col-start-2">
            <Text variant="headingSemiBold" className="!text-lg sm:!text-xl md:!text-2xl text-deep_blue shrink-0">8.</Text>
            <div className="min-w-0 flex-1">
              <Text variant="headingSemiBold" className="!text-lg sm:!text-xl md:!text-2xl text-deep_blue mb-1 sm:mb-2">Application &amp; Infrastructure Security</Text>
              <Text variant="body" className="text-deep_blue !font-FigtreeRegular !text-xs sm:!text-sm leading-relaxed">
              Secure coding, vulnerability assessments, patch management. 
              </Text>
            </div>
          </div>
          {/* 9. Logging & Monitoring */}
          <div className="flex items-start gap-3 md:col-start-1">
            <Text variant="headingSemiBold" className="!text-lg sm:!text-xl md:!text-2xl text-deep_blue shrink-0">9.</Text>
            <div className="min-w-0 flex-1">
              <Text variant="headingSemiBold" className="!text-lg sm:!text-xl md:!text-2xl text-deep_blue mb-1 sm:mb-2">Logging &amp; Monitoring</Text>
              <Text variant="body" className="text-deep_blue !font-FigtreeRegular !text-xs sm:!text-sm leading-relaxed">
                Public, Internal, Confidential, and Sensitive Personal / Health Data.
              </Text>
            </div>
          </div>
          {/* 10. Incident Management */}
          <div className="flex items-start gap-3 md:col-start-2">
            <Text variant="headingSemiBold" className="!text-lg sm:!text-xl md:!text-2xl text-deep_blue shrink-0">10.</Text>
            <div className="min-w-0 flex-1">
              <Text variant="headingSemiBold" className="!text-lg sm:!text-xl md:!text-2xl text-deep_blue mb-1 sm:mb-2">Incident Management</Text>
              <Text variant="body" className="text-deep_blue !font-FigtreeRegular !text-xs sm:!text-sm leading-relaxed">
              Defined incident response and breach handling. 
              </Text>
            </div>
          </div>
          {/* 11. Backup & Business Continuity */}
          <div className="flex items-start gap-3 md:col-start-1">
            <Text variant="headingSemiBold" className="!text-lg sm:!text-xl md:!text-2xl text-deep_blue shrink-0">11.</Text>
            <div className="min-w-0 flex-1">
              <Text variant="headingSemiBold" className="!text-lg sm:!text-xl md:!text-2xl text-deep_blue mb-1 sm:mb-2">Backup &amp; Business Continuity</Text>
              <Text variant="body" className="text-deep_blue !font-FigtreeRegular !text-xs sm:!text-sm leading-relaxed">
                Encrypted backups and recovery testing.
              </Text>
            </div>
          </div>
          {/* 12. Third-Party Security */}
          <div className="flex items-start gap-3 md:col-start-2">
            <Text variant="headingSemiBold" className="!text-lg sm:!text-xl md:!text-2xl text-deep_blue shrink-0">12.</Text>
            <div className="min-w-0 flex-1">
              <Text variant="headingSemiBold" className="!text-lg sm:!text-xl md:!text-2xl text-deep_blue mb-1 sm:mb-2">Third-Party Security</Text>
              <Text variant="body" className="text-deep_blue !font-FigtreeRegular !text-xs sm:!text-sm leading-relaxed">
              Vendor assessments and contractual safeguards. 
              </Text>
            </div>
          </div>
          {/* 13. Regulatory Compliance - full width on md+ */}
          <div className="flex items-start gap-3 md:col-start-1 md:col-span-2">
            <Text variant="headingSemiBold" className="!text-lg sm:!text-xl md:!text-2xl text-deep_blue shrink-0">13.</Text>
            <div className="min-w-0 flex-1">
              <Text variant="headingSemiBold" className="!text-lg sm:!text-xl md:!text-2xl text-deep_blue mb-1 sm:mb-2">Regulatory Compliance</Text>
              <Text variant="body" className="text-deep_blue !font-FigtreeRegular !text-xs sm:!text-sm leading-relaxed mb-2">
                Aligned with IT Act 2000 (India), ISO 27001, SOC 2, HIPAA (where applicable).
              </Text>
              <Text variant="body" className="text-deep_blue !font-FigtreeSemiBold !text-xs sm:!text-sm leading-relaxed mt-6 mb-1">Annexure A: ISO 27001 / SOC 2 / HIPAA Alignment</Text>
              <Text variant="body" className="text-deep_blue !font-FigtreeRegular !text-xs sm:!text-sm leading-relaxed mb-4">
                Second Opinion aligns its ISMS with global standards covering access control, encryption, risk management, and audit logging.
              </Text>
              <Text variant="body" className="text-deep_blue !font-FigtreeSemiBold !text-xs sm:!text-sm leading-relaxed mb-1">Annexure B: Data Privacy Policy</Text>
              <Text variant="body" className="text-deep_blue !font-FigtreeRegular !text-xs sm:!text-sm leading-relaxed mb-4">
                Covers consent, lawful processing, data minimization, retention, user rights, and breach notification.
              </Text>
              <Text variant="body" className="text-deep_blue !font-FigtreeSemiBold !text-xs sm:!text-sm leading-relaxed mb-1">Annexure C: Access Control SOP</Text>
              <Text variant="body" className="text-deep_blue !font-FigtreeRegular !text-xs sm:!text-sm leading-relaxed mb-4">
                Defines access provisioning, MFA, reviews, revocation, and audit trails.
              </Text>
              <Text variant="body" className="text-deep_blue !font-FigtreeSemiBold !text-xs sm:!text-sm leading-relaxed mb-1">Annexure D: Mobile App &amp; Cloud Security</Text>
              <Text variant="body" className="text-deep_blue !font-FigtreeRegular !text-xs sm:!text-sm leading-relaxed">
                Covers mobile app security, AWS/Azure/GCP IAM, encryption, logging, and backups.
              </Text>
            </div>
          </div>
        </div>
      </div>

      {/* Logo below content */}
      <div className="mb-6 sm:mb-8">
        <Logo imageStyle="w-[80px] sm:w-[90px] md:w-[120px]" textStyle="ml-1 text-lg sm:text-xl md:text-2xl" />
      </div>
    </div>
  );
};
